import React, { useEffect, useState } from 'react';
import { CircularProgress, IconButton } from '@mui/material';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CheckIcon from '@mui/icons-material/Check';
import UndoIcon from '@mui/icons-material/Undo';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import styles from './styles.module.scss';
import { correctGrammarIssues } from '@documents/sagas';
import TinyMCEditor from '@common/components/TinyMCEditor';
import { isFunc } from '@utils/functions';

const MIN_TEXT_LENGTH = 2;
const MAX_TEXT_LENGTH = 20000;

const EnhancedTinyMCEditor = ({
  state,
  updateState,
  shouldUpdate = false,
  setShouldUpdate,
  disabled = false,
  ...props
}) => {
  const dispatch = useDispatch();

  const [previousText, setPreviousText] = useState(undefined);
  const [isFixingGrammar, setIsFixingGrammar] = useState(false);

  const text = state ?? '';
  const isValueValidForGrammarFix = text.length >= MIN_TEXT_LENGTH && text.length <= MAX_TEXT_LENGTH;
  const isEditorDisabled = disabled || isFixingGrammar;
  const isGrammarFixDisabled = isEditorDisabled || !isValueValidForGrammarFix;
  const isReviewingCorrection = previousText !== undefined;

  useEffect(() => {
    setPreviousText(undefined);
    setIsFixingGrammar(false);
  }, [disabled]);

  const updateEditorState = (nextValue) => {
    if (isFunc(updateState)) updateState(nextValue);
    if (isFunc(setShouldUpdate)) setShouldUpdate(true);
  };

  const handleFixGrammar = () => {
    if (isGrammarFixDisabled) return;

    const originalText = text;
    setIsFixingGrammar(true);

    dispatch(correctGrammarIssues({
      text: originalText,
      onSuccess: (data) => {
        if (data?.correctedText !== undefined) {
          setPreviousText(originalText);
          updateEditorState(data.correctedText);
        }
        setIsFixingGrammar(false);
      },
      onError: () => {
        setIsFixingGrammar(false);
      }
    }));
  };

  const handleCancelCorrection = () => {
    updateEditorState(previousText ?? '');
    setPreviousText(undefined);
  };

  const handleAcceptCorrection = () => {
    setPreviousText(undefined);
  };

  return (<>
    <TinyMCEditor
      {...props}
      state={state}
      updateState={updateState}
      shouldUpdate={shouldUpdate}
      setShouldUpdate={setShouldUpdate}
      disabled={isEditorDisabled}
    />
    <div className={styles.actions}>
      {isFixingGrammar ? (<>
        <CircularProgress size={15} className={styles.buttonProgress} />
      </>) : (<>
        {isReviewingCorrection ? (
          <>
            <IconButton
              className={styles.iconButton}
              onClick={handleCancelCorrection}
              disabled={disabled}
              size="small"
              aria-label="restore previous text"
            >
              <UndoIcon />
            </IconButton>
            <IconButton
              className={`${styles.iconButton} ${styles.acceptIcon}`}
              onClick={handleAcceptCorrection}
              disabled={disabled}
              size="small"
              aria-label="accept corrected text"
            >
              <CheckIcon />
            </IconButton>
          </>
        ) : (
          <IconButton
            className={styles.iconButton}
            onClick={handleFixGrammar}
            disabled={isGrammarFixDisabled}
            size="small"
            aria-label="fix grammar"
          >
            <AutoFixHighIcon />
          </IconButton>
        )}
      </>)}
      <span className={styles.actionDescription}>{!!isReviewingCorrection ? 'Improvement complete. Keep changes?' : (!!isFixingGrammar ? 'Improving text...' : 'Improve your text')}</span>
    </div>
  </>);
};

EnhancedTinyMCEditor.propTypes = {
  state: PropTypes.string,
  updateState: PropTypes.func,
  shouldUpdate: PropTypes.bool,
  setShouldUpdate: PropTypes.func,
  disabled: PropTypes.bool
};

export default EnhancedTinyMCEditor;
